import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatExpansionPanel } from '@angular/material/expansion';
import { Options } from '@angular-slider/ngx-slider';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import {
  HourlyConfigColumn,
  PermissionEnum,
  PricesToOffer,
  ProgramConfig,
  realTime,
  RealTimeMarketConfig,
  Site,
} from '@model';
import {
  BidService,
  BidStrategyService,
  ForecastService,
  GlobalAlertService,
  MarketsConfigService,
  PermissionService,
  SiteService,
} from '@service';
import { HourlyConfigModalComponent } from '../../shared/component/hourly-config-modal/hourly-config-modal.component';
import { RealTimeOverrideConfig } from '../../shared/model/real-time-market.model';
import { ExportModalComponent } from '../export-modal/export-modal.component';

@Component({
  selector: 'bmd-rt-market',
  // eslint-disable-next-line @angular-eslint/prefer-standalone
  standalone: false,
  styleUrls: ['./real-time-market.component.css'],
  templateUrl: './real-time-market.component.html',
  viewProviders: [MatExpansionPanel],
})
export class RealTimeMarketComponent implements OnInit, OnDestroy, OnChanges {
  @Input() site: Site;
  @Input() cron: string;
  private subject$: Subject<void>;

  defaultPricesToOfferRealTime: PricesToOffer;
  pricesToOfferRealTime: PricesToOffer;

  realTimeForm = new FormGroup({
    energyMarketRealTime: new FormControl(false),
    maxPriceEnergyRealTime: new FormControl(0, Validators.compose([Validators.required, Validators.min(0)])),
    nonSpinResponsibilityPrice: new FormControl(0, Validators.compose([Validators.required, Validators.min(0)])),
    realTimeForecaster: new FormControl(''),
  });

  value = 0;
  riskProfileRealTime = 0;
  options: Options = {
    showTicksValues: true,
    stepsArray: [
      { legend: 'Low', value: 0 },
      { legend: 'Medium', value: 50 },
      { legend: 'High', value: 100 },
    ],
  };

  constructor(
    private alertService: GlobalAlertService,
    private marketsConfigService: MarketsConfigService,
    private bidStrategyService: BidStrategyService,
    private permissionService: PermissionService,
    private siteService: SiteService,
    public dialog: MatDialog,
  ) {
    this.defaultPricesToOfferRealTime = {
      extraConf: { aggressivity: '', method: '' },
      maxPrice: 105,
      minPrice: 0,
      nonSpin: 5000,
      strategy: 'taker',
    };
  }

  ngOnInit() {
    this.subject$ = new Subject<void>();
    this.initForm();
  }

  // Comment out risk profile for real time, not being used yet per Dylan 09/21/2021.
  private initForm() {
    if (this.site) {
      this.pricesToOfferRealTime = this.site.config.pricesToOfferEnergyRealTime
        ? this.site.config.pricesToOfferEnergyRealTime
        : this.defaultPricesToOfferRealTime;
      this.riskProfileRealTime = this.site.config.riskProfileRealTime ? this.site.config.riskProfileRealTime : 0;

      this.realTimeForm = new FormGroup({
        energyMarketRealTime: new FormControl(
          this.site.config.energyMarketRealTime && this.site.config.energyMarketRealTime === 'true',
        ),
        maxPriceEnergyRealTime: new FormControl(
          this.pricesToOfferRealTime.maxPrice,
          Validators.compose([Validators.required, Validators.min(0)]),
        ),
        nonSpinResponsibilityPrice: new FormControl(
          this.pricesToOfferRealTime.nonSpin,
          Validators.compose([Validators.required, Validators.min(0)]),
        ),
        realTimeForecaster: new FormControl(this.site.config.realTimeForecaster),
      });
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.initForm();
  }

  ngOnDestroy() {
    this.subject$.next();
    this.subject$.complete();
  }

  save() {
    this.pricesToOfferRealTime.maxPrice = this.realTimeForm.get('maxPriceEnergyRealTime').value;
    this.pricesToOfferRealTime.nonSpin = this.realTimeForm.get('nonSpinResponsibilityPrice').value;

    const configuration: RealTimeMarketConfig = {
      energyMarketRealTime: this.realTimeForm.get('energyMarketRealTime').value,
      hourlyIntraDayRtForecastOverride: this.realTimeForm.get('hourlyIntraDayRtForecastOverride')?.value,
      pricesToOfferEnergyRealTime: this.pricesToOfferRealTime,
      realTimeForecaster: this.realTimeForm.get('realTimeForecaster').value,
      riskProfileRealTime: this.riskProfileRealTime,
    };

    this.marketsConfigService.putRealtimeConfig(this.site, configuration).subscribe(
      data => {
        this.alertService.setSuccess('Real time configuration has been saved successfully!');
        this.realTimeForm.markAsPristine();
      },
      error => {
        console.error(error);
        this.alertService.setError('An error was detected while saving real time market configuration.');
      },
    );
  }

  cancel() {
    this.initForm();
    this.realTimeForm.reset(this.realTimeForm.value);
  }

  exportData(type: string) {
    const dialogRef = this.dialog.open(ExportModalComponent, { data: { exportType: type, site: this.site } });
    dialogRef.componentInstance.exportType = realTime;
    dialogRef.componentInstance.site = this.site;
    dialogRef.componentInstance.csvType = type;
    console.log('the dialog was opened');
  }

  createBidStrategy(isTest: boolean) {
    const testString = isTest ? ' TEST' : '';

    if (!window.confirm(`Are you sure you want to send this${testString} bid strategy?`)) {
      return;
    }

    if (this.site) {
      this.bidStrategyService
        .sendRealTimeMarketBidStrategy(this.site, isTest)
        .pipe(takeUntil(this.subject$))
        .subscribe(
          data => {
            this.alertService.setSuccess(`Real time${testString} bid strategy has been requested.`);
          },
          error => {
            console.error(error);
            this.alertService.setError(`An error was detected while creating${testString} bid strategy.`);
          },
        );
    }
  }

  hasAccess(): boolean {
    return this.permissionService.hasPermission([PermissionEnum.update]);
  }

  openRTSPPForecast(realTimeMarket: RealTimeMarketConfig) {
    this.siteService.getSite(this.site.id).subscribe(site => {
      const modalReference = this.dialog.open(HourlyConfigModalComponent, {
        data: { backdrop: 'static', size: 'lg' },
      });
      this.site = site;
      modalReference.componentInstance.fieldsToUpdate = [
        {
            value: this.site.config.hourlyIntraDayRtForecastOverride || [],
            columnName: 'Intra-Day Real Time SPP Forecast Override',
            originalPropertyName: 'hourlyIntraDayRtForecastOverride'
        } as HourlyConfigColumn<number[]>
      ];
      modalReference.componentInstance.headerText = 'Real Time Forecast Overrides';
      modalReference.componentInstance.subHeaderText = '*Resets after elapsing';
      modalReference.componentInstance.requireFields = false;
      modalReference.componentInstance.savedConfig.subscribe(savedConfig => {
        if (savedConfig !== null) {
          realTimeMarket.hourlyIntraDayRtForecastOverride = savedConfig.hourlyIntraDayRtForecastOverride;

          this.marketsConfigService.putRealtimeConfig(this.site, savedConfig).subscribe(config => {
            this.site.config.hourlyIntraDayRtForecastOverride = savedConfig.hourlyRegUpForecastOverride;
            this.realTimeForm.markAsPristine();
            this.alertService.setSuccess('Real time configuration has been saved successfully!');
            modalReference.close();
          }),
            error => {
              console.error(error);
              this.alertService.setError('An error was detected while saving Site configuration.');
              modalReference.componentInstance.showError = true;
            };
        }
      });
    });
  }
}
